import React, { useMemo, useState } from "react";
import cx from "classnames";
import _ from "lodash";
import dateformat from "dateformat";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Tooltip from "src/components/tooltip";
import { selectDateFilter, selectReportType } from "src/redux/selectors/experiences/leadsAndReports";
import { convertDatesToTimezone } from "src/services/experiences/leadsAndReports/dateUtils";
import { leadsApi } from "src/services/experiences/leadsAndReports/leads";
import { downloadAllPages, reportsApi } from "src/services/experiences/leadsAndReports/reports";
import { buildCsv, triggerCsvDownload } from "src/services/experiences/leadsAndReports/csv";
import { LEAD_REPORT_HEADER, SESSION_CSV_HEADERS } from "src/pages/experiences/leadsAndReports/constants";
import { mergeCustomFields } from "src/services/experiences/leadsAndReports/leadFields";
import { formatDurationWithLabel } from "src/pages/experiences/leadsAndReports/utils/format";
import { LeadReportHeaderField } from "src/types/experiences/leadsAndReports";
import actionsStyles from "src/components/actionbar/actionbar.module.scss";

const mapLeadRows = (records: any[], header: LeadReportHeaderField[]) =>
  _.uniqBy(records, "contact.id").map((data) => {
    const row: Record<string, unknown> = {};
    header.forEach((h) => {
      if (h.uid === "createdDate" || h.uid === "lastVisitedDate") {
        row[h.field] = dateformat(
          h.uid === "createdDate" ? data.contact?.[h.uid] : data.lastVisitedDate,
          "dd mmm yyyy",
        );
      } else if (h.uid === "timeSpent") {
        row[h.field] = formatDurationWithLabel(data[h.uid]).value;
      } else if (h.uid === "completion") {
        row[h.field] = `${Math.round(data[h.field])} %`;
      } else if (h.uid != null) {
        row[h.field] = data[h.uid] ?? data.contact?.[h.uid!] ?? "-";
      } else {
        const cf = (data.fields || []).find((f: any) => f.field === h.field);
        if (!cf) {
          row[h.field] = "-";
        } else if (typeof cf.values === "boolean") {
          row[h.field] = cf.values.toString();
        } else if (Array.isArray(cf.values)) {
          row[h.field] = cf.values.length > 0 ? cf.values.join(", ") : "-";
        } else {
          row[h.field] = cf.values || "-";
        }
      }
    });
    return row;
  });

const mapSessionRows = (records: any[]) =>
  records.map((data) => ({
    visitor: `${_.get(data, "visitor.firstName") || ""} ${_.get(data, "visitor.lastName") || ""}`.trim(),
    email: _.get(data, "visitor.email"),
    lastVisitedDate: dateformat(data.lastVisited, "dd mmm yyyy"),
    source: data.campaign?.source ?? "",
    medium: data.campaign?.medium ?? "",
    landedPage: data.url,
    city: _.get(data, "country.city", ""),
    state: _.get(data, "country.region_name", ""),
    country: _.get(data, "country.country_name", ""),
    device: data.device,
    browser: data.browser,
    time_spent: formatDurationWithLabel(data.timeSpent).value,
    completion: `${Math.round(data.completion)} %`,
  }));

interface LeadsDownloadButtonProps {
  storyboardId: string;
  disabled?: boolean;
}

export const LeadsDownloadButton: React.FC<LeadsDownloadButtonProps> = ({ storyboardId, disabled }) => {
  const location = useLocation();
  const isReports = location.pathname.includes("/reports");
  const isLeadProfile = location.pathname.includes("/leads/profile");
  const isDisabled = disabled || isLeadProfile;

  const dateFilter = useSelector(selectDateFilter);
  const reportType = useSelector(selectReportType);
  const [loading, setLoading] = useState(false);

  const leadsRequest = useMemo(
    () => ({
      storyboard: storyboardId,
      metricType: "leads",
      reportType,
      ...convertDatesToTimezone(dateFilter ?? undefined),
    }),
    [storyboardId, reportType, dateFilter],
  );

  const reportsRequest = useMemo(
    () => ({
      storyboard: storyboardId,
      reportType,
      ...convertDatesToTimezone(dateFilter ?? undefined),
    }),
    [storyboardId, reportType, dateFilter],
  );

  const handleClick = async () => {
    if (loading || isDisabled) return;
    setLoading(true);
    try {
      if (isReports) {
        const records = await downloadAllPages(
          (page, limit) => reportsApi.fetchSessionsForDownload(reportsRequest, page, limit),
          50,
        );
        triggerCsvDownload(buildCsv(SESSION_CSV_HEADERS, mapSessionRows(records)), "Reports.csv");
      } else {
        const records = await downloadAllPages(
          (page, limit) => leadsApi.fetchLeadsForDownload(leadsRequest, page, limit),
          50,
        );
        const header = mergeCustomFields(LEAD_REPORT_HEADER, records);
        const csvHeaders = header.map((h) => ({ label: h.name, key: h.field }));
        triggerCsvDownload(buildCsv(csvHeaders, mapLeadRows(records, header)), "Leads.csv");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip content={disabled ? "Download Disabled" : undefined} placement="bottom">
      <button
        className={cx(actionsStyles.item, actionsStyles.buttonItem, actionsStyles["width-3"])}
        style={{ width: "150px" }}
        disabled={isDisabled || loading}
        onClick={handleClick}
      >
        <i className={cx(actionsStyles.icon, "paperflite-download")} />
        <span className={actionsStyles.title}>{loading ? "Downloading…" : "Download"}</span>
      </button>
    </Tooltip>
  );
};
